import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { asList } from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';

const IMG = (path) => `${process.env.PUBLIC_URL}/images/${path}`;

const DUMMY_PRODUCTS = [
  {
    id: 'demo-1',
    name: 'Electronics starter kit',
    price: 5000,
    image: IMG('i_electronics.png'),
    sku: 'ELC-001',
  },
  {
    id: 'demo-2',
    name: 'I Mobile handset',
    price: 12999,
    image: IMG('i_mobile.png'),
    sku: 'MOB-220',
  },
  {
    id: 'demo-3',
    name: 'I Marts gift card',
    price: 2500,
    image: IMG('i_marts.png'),
    sku: 'MRT-050',
  },
  {
    id: 'demo-4',
    name: 'Textiles premium pack',
    price: 3499,
    image: IMG('i_textiles.png'),
    sku: 'TXT-88',
  },
  {
    id: 'demo-5',
    name: 'Trading Academy course',
    price: 8999,
    image: IMG('i_trading_academy.png'),
    sku: 'EDU-PRO',
  },
  {
    id: 'demo-6',
    name: 'Repair center toolkit',
    price: 599,
    image: IMG('i_mobile_repair_centers.png'),
    sku: 'REP-KIT',
  },
];

function GlassPanel({ children, className = '' }) {
  return (
    <div
      className={`rounded-[1.75rem] border border-white/90 bg-white/70 shadow-[0_25px_70px_-20px_rgba(79,70,229,0.18),0_8px_32px_-12px_rgba(15,23,42,0.08)] backdrop-blur-2xl ring-1 ring-indigo-950/[0.04] ${className}`}
    >
      {children}
    </div>
  );
}

function resolveImageUrl(product) {
  const raw = product.image_url || product.image || product.product_image || '';
  if (!raw) return '';
  if (typeof raw === 'string' && (raw.startsWith('http') || raw.startsWith('/'))) return raw;
  const origin = (process.env.REACT_APP_API_ENDPOINT || 'http://127.0.0.1:8000').replace(/\/$/, '');
  return `${origin}/${String(raw).replace(/^\//, '')}`;
}

function ProductCard({ product }) {
  const imageSrc = resolveImageUrl(product);
  const price = Number(product.price ?? product.cost ?? 0);
  const sku = product.sku || product.product_code || '';

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/90 bg-white/80 shadow-[0_16px_44px_-20px_rgba(79,70,229,0.12)] ring-1 ring-slate-200/60 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_56px_-18px_rgba(79,70,229,0.22)]">
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-100 to-indigo-50/40">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={product.name || 'Product'}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-4xl text-slate-300">🛍️</div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent opacity-70" />
        <span className="absolute bottom-3 left-3 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-indigo-700 shadow-sm backdrop-blur-sm">
          Catalog
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <h3
          className="line-clamp-2 min-h-[2.75rem] text-base font-semibold leading-snug text-slate-900"
          title={product.name}
        >
          {product.name}
        </h3>
        <p className="mt-2 text-xl font-black tracking-tight text-violet-600">
          ₹{price.toLocaleString('en-IN')}
        </p>
        {sku ? (
          <p className="mt-auto pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            SKU {sku}
          </p>
        ) : null}
      </div>
    </article>
  );
}

async function fetchProductList() {
  const endpoints = [API.ADMIN_PRODUCTS, API.PRODUCTS_CATALOG];

  for (const url of endpoints) {
    try {
      const res = await api.get(url);
      const data = res.data;
      if (data?.success === false) continue;
      if (data?.success === true && Array.isArray(data.results)) {
        return { products: data.results, fallback: false };
      }
      const list = asList(data);
      if (list.length > 0) {
        return { products: list, fallback: false };
      }
    } catch {
      /* try next endpoint */
    }
  }

  return { products: DUMMY_PRODUCTS, fallback: true };
}

function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      if (!user) {
        if (!cancelled) {
          setProducts(DUMMY_PRODUCTS);
          setUseFallback(true);
          setLoading(false);
        }
        return;
      }

      const { products: list, fallback } = await fetchProductList();
      if (cancelled) return;
      const active = list.filter((p) => p.is_active !== false);
      setProducts(active.length ? active : list);
      setUseFallback(fallback);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 via-indigo-50/35 to-cyan-50/40 text-slate-800">
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-white/80 via-transparent to-cyan-50/30" aria-hidden />
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.85]"
        style={{
          background:
            'radial-gradient(ellipse 85% 50% at 50% -10%, rgba(165,180,252,0.35), transparent 50%), radial-gradient(ellipse 45% 38% at 100% 5%, rgba(207,250,254,0.7), transparent 45%), radial-gradient(ellipse 40% 35% at 0% 35%, rgba(199,210,254,0.45), transparent 42%)',
        }}
        aria-hidden
      />
      <div className="pointer-events-none fixed bottom-0 left-1/2 h-[min(55vh,480px)] w-[min(92vw,880px)] -translate-x-1/2 rounded-full bg-gradient-to-t from-cyan-200/25 via-indigo-200/20 to-transparent blur-3xl" aria-hidden />

      <div className="relative z-10">
        <section className="px-4 pb-10 pt-10 sm:pb-12 sm:pt-14">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200/80 bg-white/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-700 shadow-md shadow-indigo-200/30 backdrop-blur-md">
              ICONNECT LLP
            </div>
            <h1
              className="bg-gradient-to-br from-slate-900 via-indigo-800 to-cyan-700 bg-clip-text text-3xl font-semibold leading-tight tracking-tight text-transparent sm:text-4xl lg:text-5xl"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              Product catalog
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Browse rewards and redemption items available through the SuccessFlow programme. This is a
              read-only catalog — contact your upline or support for redemption details.
            </p>
          </div>
        </section>

        <section className="px-4 pb-12 sm:pb-16">
          <div className="mx-auto max-w-6xl">
            <GlassPanel className="p-6 sm:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-indigo-600">
                    Available items
                  </p>
                  <h2
                    className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl"
                    style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                  >
                    {loading ? 'Loading products…' : `${products.length} product${products.length !== 1 ? 's' : ''}`}
                  </h2>
                </div>
                {!user && (
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-300/40 transition hover:bg-indigo-700 no-underline"
                  >
                    Sign in for live catalog
                  </Link>
                )}
              </div>

              {useFallback && !loading && (
                <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                  <span className="text-lg" aria-hidden>
                    📦
                  </span>
                  <p className="leading-relaxed">
                    Showing sample catalog preview. Sign in or try again later to load the latest products
                    from the server.
                  </p>
                </div>
              )}
            </GlassPanel>
          </div>
        </section>

        <section className="px-4 pb-20 sm:pb-24">
          <div className="mx-auto max-w-6xl">
            {loading && products.length === 0 ? null : products.length === 0 ? (
              <GlassPanel className="py-16 text-center">
                <p className="text-4xl mb-3">🛍️</p>
                <p className="text-lg font-semibold text-slate-800">No products available</p>
                <p className="mt-2 text-sm text-slate-500">Check back soon for new catalog items.</p>
              </GlassPanel>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-slate-500 sm:text-sm">
              Prices and availability are subject to programme rules. Images are for illustration; actual
              items may vary. This page is view-only — purchases and redemptions are handled separately
              through your plan and admin verification.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Products;
